import {useEffect, useState, useRef} from "react";
import {useSelector} from "react-redux";
import {DashboardHeader} from "../../components/dashboard-header";
import {SideNav} from "../../components/dashboard-sidebar";
import {RootState} from "../../redux/store";
import {
  DashboardContainer,
  DashboardHeaderContainer,
  DashboardMainContainer,
  StyledMainSectionContainer,
  StyledMainSectionInnerContainer,
} from "./dashboard-wrapper.style";
import ReactGA from "react-ga";
import {useGetSavedCategories} from "../../react-query-hooks/category";
import {Helmet} from "react-helmet-async";
import {Navigate, useNavigate, useLocation} from "react-router-dom";
import {Alert, Button} from "antd";
import {usePricingCurrencies} from "@/features/currencies";
import {ErrorBoundary} from "react-error-boundary";
import {useGetKycStatusContext} from "../../kyc/useCases/get-kyc-status/context/get-kyc-status";

declare const window: Window &
  typeof globalThis & {
    Intercom: any;
    heap: any;
  };

interface IDashboardWrapper {
  children: React.ReactNode;
  pageTitle: string;
  pageDesc?: string;
  pageSlug?: string;
  wideContent?: boolean;
}

const DashboardWrapper: React.FC<IDashboardWrapper> = ({
  children,
  pageTitle,
  pageDesc,
  pageSlug,
  wideContent = true,
}) => {
  const {userInfo} = useSelector((state: RootState) => state.userLogin);
  const {activeContext, isContextSwitching} = useSelector(
    (state: RootState) => state.userContext
  );
  const location = useLocation();
  const [showNav, setShowNav] = useState(false);

  const navigate = useNavigate();
  const getSavedCategoriesQuery = useGetSavedCategories();
  const originalBodyOverflow = useRef<string>("");

  const toggle = () => {
    setShowNav(!showNav);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    ReactGA.pageview(window.location.pathname);
  }, []);

  const currentContext = activeContext || (userInfo?.isCustomer ? "customer" : "coach");

  useEffect(() => {
    console.log({userInfo, currentContext, activeContext});
    if (userInfo) {
      if (currentContext === "customer" && !location.pathname.startsWith("/customer")) {
        navigate("/customer/home");
        return;
      }
      if (currentContext === "coach" && location.pathname.startsWith("/customer")) {
        if (location.pathname.includes("customers")) return;
        navigate("/home");
        return;
      }
    }
  }, [currentContext, location.pathname, navigate, userInfo, isContextSwitching]);

  useEffect(() => {
    if (
      userInfo &&
      userInfo.user &&
      userInfo.user.email &&
      userInfo.user.firstName &&
      userInfo.user.lastName
    ) {
      const email = userInfo.user.email.toLowerCase();
      const name = `${userInfo.user.firstName} ${userInfo.user.lastName}`;
      const userId = userInfo.user.id;

      if (window.heap) {
        window.heap.identify(`${userId}: ${name}`);
        window.heap.addUserProperties({
          name: name,
          email: email,
          user_id: userId,
        });
      }

      if (window.Intercom) {
        window.Intercom("boot", {
          api_base: "https://api-iam.intercom.io",
          app_id: "lhv6fa0p",
          name: name,
          email: email,
          user_id: userId,
          created_at: userInfo.user.createdAt,
        });
      }
    }
  }, [userInfo]);

  useEffect(() => {
    if (originalBodyOverflow.current === "") {
      originalBodyOverflow.current = document.body.style.overflow || "";
    }

    const isMobileView = window.innerWidth <= 960;

    const handleResize = () => {
      const isMobile = window.innerWidth <= 960;
      if (!isMobile) {
        document.body.style.overflow = originalBodyOverflow.current;
      } else if (showNav) {
        document.body.style.overflow = "hidden";
      }
    };

    if (showNav && isMobileView) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = originalBodyOverflow.current;
    }

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      document.body.style.overflow = originalBodyOverflow.current;
    };
  }, [showNav]);

  if (
    (getSavedCategoriesQuery.data && getSavedCategoriesQuery.data.length < 1) ||
    getSavedCategoriesQuery.isError
  ) {
    return <Navigate to="/onboarding" />;
  }

  return (
    <>
      <Helmet>
        <title>Coachli | {pageTitle ?? "Do more with Coachli"} </title>
        <meta
          name="description"
          content={
            pageDesc ||
            "The simplest way to host paid 1:1 calls with your clients, mentees and audience."
          }
        />
        <link rel="canonical" href={pageSlug} />
        <meta property="og:url" content={pageSlug} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:image:width" content="" />
        <meta property="og:image:height" content="" />
        {/* <meta property="og:image" content={image} />
            <meta name="og:image" content={image} /> */}
        <meta property="og:type" content="website" />
        <meta charSet="utf-8" />
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
        <link rel="icon" href="/favicon.ico" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        {/* <meta property="twitter:image" content={image} /> */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:image:alt" content="Coachli Logo" />
        <meta name="twitter:site" content="" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:url" content={pageSlug} />
      </Helmet>
      <DashboardContainer>
        <SideNav toggleNav={toggle} visible={showNav} />
        <DashboardMainContainer>
          <DashboardHeaderContainer>
            <DashboardHeader toggleNav={toggle} />
          </DashboardHeaderContainer>
          <StyledMainSectionContainer id="main-section-container">
            <StyledMainSectionInnerContainer $wide={wideContent}>
              <ErrorBoundary
                FallbackComponent={({error, resetErrorBoundary}) => (
                  <Alert
                    message="Sorry, something went wrong."
                    showIcon
                    description={error.message}
                    type="error"
                    action={
                      <Button
                        onClick={() => {
                          resetErrorBoundary();
                        }}
                        size="small"
                        danger
                      >
                        Reset
                      </Button>
                    }
                  />
                )}
              >
                {children}
              </ErrorBoundary>
            </StyledMainSectionInnerContainer>
          </StyledMainSectionContainer>
        </DashboardMainContainer>
      </DashboardContainer>
    </>
  );
};

export {DashboardWrapper};
